import { useEffect, useRef, useState } from "react";
import { useIsDesktop } from "../../utils/isDesktop";
import {
  GoABlock,
  GoAButton,
  GoAIcon,
  GoASpacer,
} from "@abgov/react-components";

import styles from "./media-manager.module.css";
import axios, { AxiosInstance, AxiosError } from "axios";

export const loadImage = async (
  filename: string,
  csrService: AxiosInstance,
  retries = 3,
  delay = 1000
): Promise<string> => {
  try {
    const result = await csrService.get(
      "api/v1/media/images/collisions/" + filename,
      {
        responseType: "arraybuffer",
        timeout: 5000,
        headers: {
          "Content-Type": "image/png",
        },
      }
    );
    return Promise.resolve<string>(result.data);
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && retries > 0) {
      console.warn(`Retrying... (${retries} attempts left)`);
      await new Promise((res) => setTimeout(res, delay));
      return loadImage(filename, csrService, retries - 1, delay * 2);
    }
    return Promise.reject<string>("Error loading image");
  }
};

export const ImageCard = ({
  fileName,
  axiosInstance,
  showDeleteFile,
  deleteFile,
  imagePromise,
  newKey,
  height = 150,
  width = 300,
  showMedaLinks = true,
  showBorder = true,
  onLoadingComplete,
}: {
  fileName: string;
  axiosInstance: AxiosInstance;
  showDeleteFile?: boolean;
  deleteFile: (fileName: string) => void;
  imagePromise: (
    fileName: string,
    csrService: AxiosInstance,
    retries?: number,
    delay?: number
  ) => Promise<string>;
  newKey?: string;
  height?: number;
  width?: number;
  showMedaLinks?: boolean;
  showBorder?: boolean;
  onLoadingComplete?: () => void;
}) => {
  const isDesktop = useIsDesktop();

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [showFullSize, setShowFullSize] = useState(false);
  const [fullSizeSrc, setFullSizeSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const previousState = useRef(loading);

  useEffect(() => {
    previousState.current = loading;

    if (!loading && onLoadingComplete) onLoadingComplete();
  }, [loading]);
  const createThumbnail = async (
    blob: Blob,
    maxWidth: number,
    maxHeight: number
  ): Promise<{ thumbnailBlob: Blob; fullsizeUrl: string }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const fullsizeUrl = URL.createObjectURL(blob);

      img.src = fullsizeUrl;

      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;

        const canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;

        const ctx = canvas.getContext("2d");

        if (!ctx) {
          URL.revokeObjectURL(fullsizeUrl);
          reject("Error creating canvas context");
          return;
        }

        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        canvas.toBlob((thumbnailBlob) => {
          if (thumbnailBlob) {
            resolve({ fullsizeUrl, thumbnailBlob });
          } else {
            reject("Error creating thumbnail");
          }
        });
      };

      img.onerror = () => {
        reject("Error loading image");
      };
    });
  };

  useEffect(() => {
    let isMounted = true;
    if (!imageSrc && !fullSizeSrc) {
      imagePromise(fileName, axiosInstance)
        .then(async (src) => {
          if (isMounted) {
            const blob = new Blob([src], { type: "image/png" });

            const { fullsizeUrl, thumbnailBlob } = await createThumbnail(
              blob,
              height,
              width
            );

            const thumbnailUrl = URL.createObjectURL(thumbnailBlob);

            setImageSrc(thumbnailUrl);
            setFullSizeSrc(fullsizeUrl);
            setLoading(false);
          }
        })
        .catch((error) => {
          if (isMounted) setLoading(false);
        });

      return () => {
        isMounted = false;
      };
    }
  }, []);

  return (
    <div key={newKey}>
      <div
        className={showBorder ? styles.mediacardborder : styles.mediacard}
        style={{ maxWidth: isDesktop ? "310px" : "343px" }}
        key={fileName + Date.now()}
      >
        {showBorder == true ? <GoASpacer vSpacing={"xl"} /> : ""}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div>
            {loading ? (
              "Loading..."
            ) : !imageSrc ? (
              "Error loading image"
            ) : (
              <img
                src={imageSrc}
                alt="thumbnail"
                onClick={() => {
                  setShowFullSize(true);
                }}
              />
            )}
            <div
              className={styles.modal}
              style={{ display: showFullSize ? "flex" : "none" }}
              onClick={() => {
                setShowFullSize(false);
              }}
            >
              <img src={fullSizeSrc ?? ""} alt="fullsize" />
            </div>
          </div>
          <div style={{ display: showMedaLinks ? "block" : "none" }}>
            <div style={{ width: "100%" }}>
              <GoABlock direction="row" alignment="center">
                <GoAButton
                  type="tertiary"
                  onClick={() => {
                    setShowFullSize(true);
                  }}
                >
                  <div style={{ display: "flex", alignContent: "bottom" }}>
                    {fileName
                      .substring(fileName.lastIndexOf("/") + 1)
                      .substring(0, 10) + "..."}
                    <div>
                      <GoAIcon ml={"xs"} size="small" type="open"></GoAIcon>
                    </div>
                  </div>
                </GoAButton>
                {showDeleteFile && (
                  <GoAButton
                    type="tertiary"
                    onClick={() => {
                      deleteFile(fileName);
                    }}
                  >
                    Remove
                  </GoAButton>
                )}
              </GoABlock>
            </div>
          </div>
        </div>
        {showBorder == true ? <GoASpacer vSpacing={"xl"} /> : ""}
      </div>
      {showBorder == true ? <GoASpacer vSpacing={"xl"} /> : ""}
    </div>
  );
};
